import React from 'react';
import styled from 'styled-components';

const Section = styled.section`
  padding: 120px 0;
  background: var(--gradient-surface);
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    width: 1000px;
    height: 1000px;
    border-radius: 50%;
    background: var(--gradient-primary);
    bottom: -500px;
    left: -300px;
    opacity: 0.05;
    animation: pulse 8s ease-in-out infinite;
  }

  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const IPadFrame = styled.div`
  background: var(--surface);
  border-radius: 40px;
  padding: 20px;
  box-shadow: var(--shadow-lg);
  width: 1000px;
  max-width: 95%;
  margin: 0 auto;
  transition: all var(--transition-normal);
  border: 1px solid var(--border);
  position: relative;

  &:hover {
    transform: translateY(-10px);
    box-shadow: var(--shadow-primary);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-primary);
    border-radius: 4px 4px 0 0;
  }
`;

const Screen = styled.div`
  background: var(--background);
  border-radius: 24px;
  overflow: hidden;
  height: 600px;
  padding: 24px;
  transition: all var(--transition-normal);
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding: 0 16px;
`;

const Logo = styled.div`
  font-size: 24px;
  font-weight: 800;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Nav = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;

  a {
    text-decoration: none;
    color: var(--text-secondary);
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 12px;
    transition: all var(--transition-normal);

    &:hover {
      color: var(--primary);
      background: var(--surface-hover);
      transform: translateY(-2px);
    }

    sup {
      background: var(--primary);
      color: white;
      padding: 2px 6px;
      border-radius: 8px;
      font-size: 10px;
    }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 32px;
  animation: fadeIn var(--transition-normal) forwards;
`;

const StatCard = styled.div`
  background: var(--surface);
  border-radius: 16px;
  padding: 24px;
  box-shadow: var(--shadow-sm);
  transition: all var(--transition-normal);

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-md);
    background: var(--gradient-primary);
    color: white;

    .icon {
      background: rgba(255, 255, 255, 0.2);
    }

    .value, .label {
      color: white;
    }
  }

  .icon {
    width: 40px;
    height: 40px;
    background: var(--primary-light);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    transition: all var(--transition-normal);
  }

  .value {
    font-size: 32px;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 8px;
    transition: all var(--transition-normal);
  }

  .label {
    color: var(--text-secondary);
    font-size: 14px;
    transition: all var(--transition-normal);
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
  animation: slideIn var(--transition-normal) forwards;
`;

const ChartCard = styled.div`
  background: var(--surface);
  border-radius: 16px;
  padding: 24px;
  box-shadow: var(--shadow-sm);
  transition: all var(--transition-normal);

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-md);
  }

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 16px;
  }
`;

const DueTable = styled.div`
  background: var(--surface);
  border-radius: 16px;
  padding: 24px;
  box-shadow: var(--shadow-sm);
  transition: all var(--transition-normal);
  animation: fadeIn var(--transition-normal) forwards;

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-md);
  }

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 16px;
  }

  table {
    width: 100%;
    border-collapse: collapse;

    th, td {
      padding: 12px;
      text-align: left;
      border-bottom: 1px solid var(--border);
      font-size: 14px;
    }

    th {
      color: var(--text-secondary);
      font-weight: 500;
    }

    td {
      color: var(--text-primary);
    }

    .monthly {
      color: var(--primary);
      font-weight: 500;
    }

    .date {
      color: var(--error);
      font-weight: 500;
    }

    tr {
      transition: all var(--transition-normal);

      &:hover {
        background: var(--surface-hover);
      }
    }
  }
`;

const DashboardFeature = () => {
  return (
    <Section>
      <Container>
        <IPadFrame>
          <Screen>
            <TopBar>
              <Logo>nexpend.</Logo>
              <Nav>
                <a href="#"><span>🏠</span> Dashboard</a>
                <a href="#"><span>📋</span> Subscriptions <sup>12</sup></a>
                <a href="#"><span>💳</span> Payment Methods</a>
                <a href="#"><span>🏷️</span> Tags</a>
              </Nav>
            </TopBar>

            <StatsGrid>
              <StatCard>
                <div className="icon">📊</div>
                <div className="value">12</div>
                <div className="label">Active subscriptions</div>
              </StatCard>
              <StatCard>
                <div className="icon">💰</div>
                <div className="value">$125.49</div>
                <div className="label">Current monthly costs</div>
              </StatCard>
              <StatCard>
                <div className="icon">📅</div>
                <div className="value">$279.00</div>
                <div className="label">Current yearly costs</div>
              </StatCard>
            </StatsGrid>

            <ChartsGrid>
              <ChartCard>
                <h3>Next Months Costs</h3>
                {/* Line chart would go here */}
              </ChartCard>
              <ChartCard>
                <h3>Subscriptions Costs per Tag</h3>
                {/* Pie chart would go here */}
              </ChartCard>
            </ChartsGrid>

            <DueTable>
              <h3>Due Next Week</h3>
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Price</th>
                    <th>Method</th>
                    <th>Cycle</th>
                    <th>Due Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>iCloud</td>
                    <td>$9.00</td>
                    <td>Wise Bank Card</td>
                    <td className="monthly">Monthly</td>
                    <td className="date">04 Jan 2025</td>
                    <td>✏️</td>
                  </tr>
                  <tr>
                    <td>AI Assistant</td>
                    <td>$20.00</td>
                    <td>Wise Bank Card</td>
                    <td className="monthly">Monthly</td>
                    <td className="date">03 Jan 2025</td>
                    <td>✏️</td>
                  </tr>
                </tbody>
              </table>
            </DueTable>
          </Screen>
        </IPadFrame>
      </Container>
    </Section>
  );
};

export default DashboardFeature;